import { useCallback } from 'react'

import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { ShapeKeyType } from '@editor/interfaces/shape'
import { zeroPad } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { isMobile } from 'react-device-detect'
import { useSelector } from 'react-redux'

import { removeErrorPlanes, removePlane, resetWorkingPoints } from '../store'

const useElementsPanel = (): ElementsPanelConfig => {
  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const planes = useSelector((state: RootState) => state.toolPlaneDetection.planes)
  const savingPlanes = useSelector((state: RootState) => state.toolPlaneDetection.savingPlanes)
  const errorPlanes = useSelector((state: RootState) => state.toolPlaneDetection.errorPlanes)
  const planeWorkingPoints = useSelector((root: RootState) => root.toolPlaneDetection.workingPoints)
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)
  const userType = useSelector((state: RootState) => state.user.userType)

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']) => {
        // Surface plane is parent-level, ignore if parent is provided
        if (parent) return []

        const planeItems = inspectionItems.filter(
          (item) => item.item_type === InspectionItemType.PolygonArea && !item.volume_id,
        )

        if (planeItems.length || planes.length || planeWorkingPoints.length) {
          return [
            {
              key: 'panel-surface-plane',
              name: '面積',
              isVirtualContainer: true,
              isCountable: true,
              children: [
                ...(planeItems
                  .map<EditorPanelItem | null>((item, index) => {
                    const poly = shapes.polygons.find((shape) => item.shape_ids.polygons.includes(shape.shape_id))
                    if (!poly) {
                      return null
                    }

                    return {
                      key: `panel-surface-plane-${item.inspection_item_id}`,
                      name: item?.part_name || `面積${zeroPad(index + 1, 3)}`,
                      shapeKey: ShapeKeyType.POLYGON,
                      item: poly,
                      inspectionItem: item,
                      order: poly.order_in_inspection_group,
                      isGrouped: !!poly.inspection_group_id,
                      isOrderable: true,
                      onDelete: isAllowedToModify,
                    }
                  })
                  .filter(Boolean) as EditorPanelItem[]),
                ...savingPlanes.map<EditorPanelItem>((plane, index) => ({
                  key: `panel-surface-saving-plane-${plane.id}`,
                  name: `面積${zeroPad(index + planeItems.length + 1, 3)}`,
                  shapeKey: ShapeKeyType.POLYGON,
                  item: plane,
                  isWorking: true,
                  isSaving: true,
                  onDelete: false, // Cannot delete saving planes
                })),
                ...errorPlanes.map((plane, index) => ({
                  key: `panel-surface-error-plane-${plane.id}`,
                  name: `面積${zeroPad(index + planeItems.length + savingPlanes.length + 1, 3)}`,
                  shapeKey: ShapeKeyType.POLYGON,
                  item: plane,
                  errorMessage: plane.error,
                  onDelete: () => dispatch(removeErrorPlanes([plane.id])),
                })),
                ...planes.map((plane, index) => ({
                  key: `panel-surface-working-plane-${plane.id}`,
                  name: `面積${zeroPad(index + savingPlanes.length + planeItems.length + errorPlanes.length + 1, 3)}`,
                  shapeKey: ShapeKeyType.POLYGON,
                  item: plane,
                  isWorking: true,
                  onDelete: () => dispatch(removePlane(index)),
                })),
                ...(planeWorkingPoints.length > (isMobile ? 0 : 1)
                  ? [
                      {
                        key: 'panel-surface-plane-working',
                        name: `面積${zeroPad(planes.length + savingPlanes.length + errorPlanes.length + planeItems.length + 1, 3)}`,
                        shapeKey: ShapeKeyType.POLYGON,
                        toggleVisibility: null,
                        isWorking: true,
                        onDelete: () => dispatch(resetWorkingPoints()),
                      },
                    ]
                  : []),
              ].filter(Boolean),
            },
          ]
        }

        return []
      },
      [
        inspectionItems,
        shapes,
        planes,
        savingPlanes,
        errorPlanes,
        planeWorkingPoints.length,
        isAllowedToModify,
        dispatch,
      ],
    ),
  }
}

export default useElementsPanel
